import { z } from "zod";

/**
 * A vendor order (Skiddle, AltTickets) of the signed-in owner's tickets for
 * an access, as My Tickets shows it in the app and on Link in Bio.
 * `awaiting_release`: the vendor has not released the codes yet.
 * `released`: the codes are the vendor's own, exactly as it states them.
 * `processing`: the order exists but has no codes yet (collection pending or
 * failed). `tickets` is only ever set for `released`, and only for the owner.
 */
export const vendorOrderViewSchema = z.object({
  status: z.enum(["awaiting_release", "released", "processing"]),
  /** The vendor's 'available from' instant, ISO 8601. */
  availableAt: z.string().optional(),
  /** `availableAt` in the event's timezone, e.g. "Tue 6 Oct, 7pm". */
  availableAtLabel: z.string().optional(),
  tickets: z
    .array(
      z.object({
        position: z.number(),
        label: z.string(),
        numberLast4: z.string(),
        /** The vendor's barcode type, e.g. QR_CODE. */
        codeType: z.string(),
        code: z.string(),
      }),
    )
    .optional(),
  /** The order's ticket documents, of any vendor: the tickets as one PDF
   *  (`pdf`, a short-lived signed download link) or the vendor's own page
   *  showing them (`page`). Owner-only links; absent when there are none. */
  documents: z
    .array(z.object({ kind: z.enum(["pdf", "page"]), url: z.string() }))
    .optional(),
});
export type VendorOrderView = z.infer<typeof vendorOrderViewSchema>;
export type VendorTicketDocument = NonNullable<VendorOrderView["documents"]>[number];

/**
 * A release time for people, in the event's zone: "Tue 6 Oct, 7pm" or
 * "Tue 6 Oct, 7:30pm". Falls back to Europe/London for a missing or unknown
 * zone.
 */
export function formatReleaseTime(
  value: string | Date,
  timezone: string | null | undefined,
): string {
  const instant = value instanceof Date ? value : new Date(value);
  const parts = (zone: string) =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).formatToParts(instant);
  let formatted: Intl.DateTimeFormatPart[];
  try {
    formatted = parts(timezone || "Europe/London");
  } catch {
    formatted = parts("Europe/London");
  }
  const get = (type: string) =>
    formatted.find((part) => part.type === type)?.value ?? "";
  const minute = get("minute");
  const clock = `${get("hour")}${minute === "00" ? "" : `:${minute}`}${get("dayPeriod").toLowerCase()}`;
  return `${get("weekday")} ${get("day")} ${get("month")}, ${clock}`;
}
